// Centralised Google Identity Services (GIS) initialisation.
// GIS allows only one initialize() per page. Both the One Tap prompt and the
// rendered Google button previously initialised independently, causing console
// warnings and unreliable One Tap. Centralise here: the shared credential callback
// calls store.loginWithGoogle(), success is signalled via googleLoginSuccessAt,
// errors via googleAuthError.

import { loadGsiClient } from '~~/utils/loadGsiClient'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          renderButton: (element: HTMLElement, options: Record<string, unknown>) => void
          prompt: (momentListener?: (notification: unknown) => void) => void
        }
      }
    }
  }
}

const googleAuthError = ref('')
// Timestamp of the last successful credential login (button or One Tap).
// Using an event signal instead of isLoggedIn avoids false navigation after
// session restore on reload.
const googleLoginSuccessAt = ref(0)
let initialized = false

export default function useGoogleIdentity () {
  const config = useRuntimeConfig()
  const store = useGuestStore()

  async function waitForGoogle (
    timeoutMs = 10000,
    loadOptions?: { immediate?: boolean }
  ): Promise<Window['google'] | null> {
    try {
      await loadGsiClient(loadOptions)
    } catch {
      return null
    }
    return new Promise((resolve) => {
      if (window.google?.accounts?.id) {
        resolve(window.google)
        return
      }
      const started = Date.now()
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval)
          resolve(window.google)
        } else if (Date.now() - started > timeoutMs) {
          clearInterval(interval)
          resolve(null)
        }
      }, 200)
    })
  }

  function handleCredentialResponse (response: { credential: string }) {
    googleAuthError.value = ''
    store.loginWithGoogle(response.credential)
      .then(() => {
        googleLoginSuccessAt.value = Date.now()
      })
      .catch((err: { data?: { message?: string }, message?: string }) => {
        // ofetch puts the backend body in err.data — use its message so the
        // ERROR_MESSAGES mapping in the form can translate it.
        googleAuthError.value = err?.data?.message || err?.message || 'Login failed'
      })
  }

  // Initialise GIS once and return the google object (or null if the library
  // hasn't loaded in time).
  async function ensureInitialized (loadOptions?: { immediate?: boolean }): Promise<Window['google'] | null> {
    const google = await waitForGoogle(10000, loadOptions)
    if (!google) {
      return null
    }
    if (!initialized) {
      google.accounts.id.initialize({
        client_id: config.public.guestGoogleClientId,
        callback: handleCredentialResponse,
        // Chrome requires One Tap via FedCM (covers third-party cookie deprecation).
        use_fedcm_for_prompt: true,
        cancel_on_tap_outside: true,
        auto_select: false
      })
      initialized = true
    }
    return google
  }

  return { ensureInitialized, googleAuthError, googleLoginSuccessAt }
}
